import { useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { modelIcon, modelInfocard } from '../modelImages'
import type { ATMModel } from '@atmvision/types'
import { useTheme } from '../hooks/useTheme'

type SOStatus = 'eol' | 'active' | 'legacy'

interface ZoneDist { dn: number; norte: number; sur: number; este: number }

interface ModelSpec {
  model: ATMModel
  subtitle: string
  family: string
  generation: string
  released: string
  so_status: SOStatus
  so_label: string
  fleet_count: number
  fleet_pct: string
  zones: ZoneDist
  cdm_cassettes: number
  card_reader: string
  screen: string
  connectivity: string
  feature_pills: string[]
  funcionalidades: { label: string; yes: boolean }[]
  reliability: 'Alta' | 'Media' | 'Baja'
  reliability_score: number
  top_faults: string[]
  notes: string
}

// ─── Model catalogue ──────────────────────────────────────────────────────────
const MODEL_SPECS: ModelSpec[] = [
  {
    model: 'NCR Selfserv 26', subtitle: 'Exterior Through-the-Wall Slimline',
    family: 'SS-2x Legacy', generation: 'Serie 20 — Exterior TTW', released: '2008',
    so_status: 'eol', so_label: 'Win 7 — EOL',
    fleet_count: 183, fleet_pct: '19.6%',
    zones: { dn: 69, norte: 72, sur: 26, este: 16 },
    cdm_cassettes: 4, card_reader: 'Motorizado', screen: '15" Touch',
    connectivity: '10/100 Ethernet',
    feature_pills: ['🌐 TTW Exterior', '💵 Dispenser', 'Win 7', '15" Touch'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: false },
      { label: 'Reciclaje', yes: false }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Media', reliability_score: 3,
    top_faults: ['CDM_CASHLOW_01', 'IDC_MOTORERR_11', 'SNMP_LINKDOWN'],
    notes: 'Unidad Through-the-Wall de alta presencia en sucursales históricas Banreservas. Soporte XFS limitado. SO en fase EOL, candidato a sustitución en blueprint NCR Vision.',
  },
  {
    model: 'NCR Selfserv 82', subtitle: 'Interior Multi-Function ATM & ITM',
    family: 'SS-8x Current', generation: 'Serie 80 — Interior ITM', released: '2017',
    so_status: 'active', so_label: 'Win 10 — Vigente',
    fleet_count: 179, fleet_pct: '19.2%',
    zones: { dn: 86, norte: 52, sur: 22, este: 19 },
    cdm_cassettes: 4, card_reader: 'Motorizado / DIP', screen: '19" Touch Widescreen',
    connectivity: 'Gigabit Ethernet',
    feature_pills: ['🏦 Interior', '♻️ Recycler', 'Win 10', '19" Touch', '📡 NFC'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: true },
      { label: 'Reciclaje', yes: true }, { label: 'ITM/Video', yes: true },
    ],
    reliability: 'Alta', reliability_score: 4,
    top_faults: ['CDM_CASHLOW_01', 'SNMP_AUTHERR', 'IDC_SHUTTER_13'],
    notes: 'Modelo de mayor densidad en la flota actual. Alta fiabilidad, soporte completo XFS/APTRA. SO image SS8X_15AGO2025 asignada.',
  },
  {
    model: 'NCR Selfserv 22', subtitle: 'Interior Freestanding Cash Dispenser',
    family: 'SS-2x Legacy', generation: 'Serie 20 — Interior Lobby', released: '2005',
    so_status: 'eol', so_label: 'Win XP — EOL',
    fleet_count: 174, fleet_pct: '18.6%',
    zones: { dn: 89, norte: 42, sur: 18, este: 25 },
    cdm_cassettes: 4, card_reader: 'Motorizado', screen: '15" LCD',
    connectivity: '10/100 Ethernet',
    feature_pills: ['🏦 Interior', '💵 Dispenser', 'Win XP ⚠️', '15" LCD'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: false }, { label: 'Depósito', yes: false },
      { label: 'Reciclaje', yes: false }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Baja', reliability_score: 2,
    top_faults: ['CDM_NOTEJAM_03', 'CDM_CASHLOW_01', 'IDC_MOTORERR_11', 'SNMP_COLDSTART'],
    notes: 'Modelo más antiguo de la flota. Alta tasa de fallas CDM. Windows XP sin soporte. Candidato prioritario de sustitución.',
  },
  {
    model: 'NCR Selfserv 22E', subtitle: 'Compact Interior Cash Dispenser (Mejorado)',
    family: 'SS-2x Legacy', generation: 'Serie 20E — Interior Lobby', released: '2007',
    so_status: 'eol', so_label: 'Win XP — EOL',
    fleet_count: 101, fleet_pct: '10.8%',
    zones: { dn: 48, norte: 23, sur: 12, este: 18 },
    cdm_cassettes: 4, card_reader: 'Motorizado', screen: '15" Touch',
    connectivity: '10/100 Ethernet',
    feature_pills: ['🏦 Interior', '💵 Dispenser', 'Win XP ⚠️', '15" Touch'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: false },
      { label: 'Reciclaje', yes: false }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Baja', reliability_score: 2,
    top_faults: ['CDM_NOTEJAM_03', 'IDC_MOTORERR_11', 'SNMP_LINKDOWN'],
    notes: 'Variante mejorada del SS-22, mismo chasis. Controlador CDM actualizado. Igualmente con SO Windows XP obsoleto.',
  },
  {
    model: 'NCR Selfserv 84', subtitle: 'Exterior Walk-Up Multi-Function ITM',
    family: 'SS-8x Current', generation: 'Serie 80 — Exterior ITM', released: '2018',
    so_status: 'active', so_label: 'Win 10 — Vigente',
    fleet_count: 94, fleet_pct: '10.1%',
    zones: { dn: 17, norte: 32, sur: 24, este: 21 },
    cdm_cassettes: 4, card_reader: 'Motorizado', screen: '21" Touch Widescreen',
    connectivity: 'Gigabit Ethernet',
    feature_pills: ['🌐 TTW Exterior', '♻️ Recycler', 'Win 10', '🎥 Video ITM'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: true },
      { label: 'Reciclaje', yes: true }, { label: 'ITM/Video', yes: true },
    ],
    reliability: 'Alta', reliability_score: 5,
    top_faults: ['CDM_CASHLOW_01', 'OPX_CASSETTE_SWAP'],
    notes: 'Modelo flagship de la flota actual. Capacidad de depósito BNA integrada y video-ITM. Máxima disponibilidad financiera.',
  },
  {
    model: 'NCR Selfserv 32', subtitle: 'Interior Full-Function ATM (código: 6632)',
    family: 'SS-3x Mid', generation: 'Serie 30 — Interior Mid', released: '2010',
    so_status: 'eol', so_label: 'Win XP — EOL',
    fleet_count: 47, fleet_pct: '5.0%',
    zones: { dn: 16, norte: 19, sur: 6, este: 6 },
    cdm_cassettes: 8, card_reader: 'Motorizado', screen: '15" Touch',
    connectivity: '100 Ethernet',
    feature_pills: ['🏦 Interior', '💰 Full-Function', 'Win XP ⚠️', '8 Cassettes'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: false },
      { label: 'Reciclaje', yes: false }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Media', reliability_score: 3,
    top_faults: ['CDM_CASHLOW_01', 'IDC_CARDJAM_10', 'SNMP_LINKDOWN'],
    notes: 'Serie 3x de transición. Soporte XFS parcial. En proceso de sustitución planificada en el roadmap NCR Vision.',
  },
  {
    model: 'NCR Selfserv 6682 FA', subtitle: '≡ SelfServ 82 · Front Access',
    family: 'SS-66xx Modular', generation: 'Serie 6600 — Modular Front Access', released: '2020',
    so_status: 'active', so_label: 'Win 10 — Vigente',
    fleet_count: 46, fleet_pct: '4.9%',
    zones: { dn: 28, norte: 10, sur: 6, este: 2 },
    cdm_cassettes: 6, card_reader: 'Motorizado', screen: '17" Touch Widescreen',
    connectivity: 'Gigabit Ethernet',
    feature_pills: ['🏦 Interior', '♻️ Recycler', 'Win 10', 'Front Access'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: true },
      { label: 'Reciclaje', yes: true }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Alta', reliability_score: 5,
    top_faults: ['CDM_COUNTERFEIT_05', 'OPX_CASSETTE_SWAP'],
    notes: 'Arquitectura modular con reciclador. Front Access facilita mantenimiento en espacios reducidos. Mismo software que SS-82.',
  },
  {
    model: 'NCR Selfserv 38', subtitle: 'Exterior Drive-Up Full-Function ATM',
    family: 'SS-3x Mid', generation: 'Serie 30 — Exterior Drive-Up', released: '2012',
    so_status: 'eol', so_label: 'Win XP — EOL',
    fleet_count: 36, fleet_pct: '3.9%',
    zones: { dn: 16, norte: 15, sur: 3, este: 2 },
    cdm_cassettes: 6, card_reader: 'Motorizado', screen: '15" Touch',
    connectivity: '100 Ethernet',
    feature_pills: ['🚗 Drive-Up', '💰 Full-Function', 'Win XP ⚠️', '☀️ Todo Clima'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: false },
      { label: 'Reciclaje', yes: false }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Media', reliability_score: 3,
    top_faults: ['CDM_CASHLOW_01', 'CDM_NOTEJAM_03', 'SNMP_LINKDOWN'],
    notes: 'Variante de alta capacidad Drive-Up, todo clima. 6 cassettes para alto tráfico vehicular. SO obsoleto, sin soporte.',
  },
  {
    model: 'NCR Selfserv 23', subtitle: 'Interior Premium Lobby Cash Dispenser',
    family: 'SS-2x Legacy', generation: 'Serie 20 — Compact Branch', released: '2006',
    so_status: 'eol', so_label: 'Win 7 — EOL',
    fleet_count: 35, fleet_pct: '3.8%',
    zones: { dn: 14, norte: 8, sur: 4, este: 9 },
    cdm_cassettes: 2, card_reader: 'Motorizado', screen: '10.4" LCD',
    connectivity: '10/100 Ethernet',
    feature_pills: ['🏦 Interior', '💵 Dispenser', 'Win 7 ⚠️', 'S2 Module'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: false }, { label: 'Depósito', yes: false },
      { label: 'Reciclaje', yes: false }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Baja', reliability_score: 2,
    top_faults: ['CDM_CASHLOW_01', 'IDC_MOTORERR_11', 'ISO_SYSTEMERR_96'],
    notes: 'Unidad compacta de sucursal. Solo 2 cassettes, capacidad muy limitada. Alta prioridad de sustitución.',
  },
  {
    model: 'NCR Selfserv 27', subtitle: 'Lobby Compact Cash Dispenser',
    family: 'SS-2x Legacy', generation: 'Serie 20 — Interior Lobby', released: '2009',
    so_status: 'eol', so_label: 'Win 7 — EOL',
    fleet_count: 20, fleet_pct: '2.1%',
    zones: { dn: 13, norte: 5, sur: 1, este: 1 },
    cdm_cassettes: 4, card_reader: 'Motorizado', screen: '15" Touch',
    connectivity: '100 Ethernet',
    feature_pills: ['🏦 Interior', '💵 Dispenser', 'Win 7 ⚠️', '15" Touch'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: false },
      { label: 'Reciclaje', yes: false }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Media', reliability_score: 3,
    top_faults: ['CDM_CASHLOW_01', 'IDC_CARDJAM_10', 'SNMP_LINKDOWN'],
    notes: 'Footprint reducido para instalaciones comerciales. Pendiente de reemplazo.',
  },
  {
    model: 'NCR Selfserv 88', subtitle: 'Interior Full-Function ATM & ITM',
    family: 'SS-8x Current', generation: 'Serie 80 — Interior Full', released: '2019',
    so_status: 'active', so_label: 'Win 10 — Vigente',
    fleet_count: 9, fleet_pct: '1.0%',
    zones: { dn: 5, norte: 3, sur: 0, este: 1 },
    cdm_cassettes: 4, card_reader: 'Motorizado', screen: '21" Touch Widescreen',
    connectivity: 'Gigabit Ethernet',
    feature_pills: ['🏦 Interior', '♻️ Recycler', 'Win 10', '21" Touch', '📡 NFC'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: true },
      { label: 'Reciclaje', yes: true }, { label: 'ITM/Video', yes: true },
    ],
    reliability: 'Alta', reliability_score: 5,
    top_faults: ['CDM_CASHLOW_01'],
    notes: 'Unidad de máxima funcionalidad de la flota actual. Pantalla 21", NFC, reciclador. Para sucursales de alto tráfico.',
  },
  {
    model: 'GRG H22VL', subtitle: 'Exterior Lobby / Through-the-Wall',
    family: 'GRG Non-NCR', generation: 'GRG Banking — Exterior TTW', released: '2015',
    so_status: 'legacy', so_label: 'Win 7 — Legacy',
    fleet_count: 6, fleet_pct: '0.6%',
    zones: { dn: 6, norte: 0, sur: 0, este: 0 },
    cdm_cassettes: 4, card_reader: 'Motorizado', screen: '15" Touch',
    connectivity: '100 Ethernet',
    feature_pills: ['🌐 TTW Exterior', '💵 Dispenser', 'Win 7', '⚠️ Non-NCR'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: false }, { label: 'Depósito', yes: false },
      { label: 'Reciclaje', yes: false }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Media', reliability_score: 3,
    top_faults: ['IDC_MOTORERR_11', 'CDM_NOTEJAM_03', 'SNMP_LINKDOWN', 'ISO_SYSTEMERR_96'],
    notes: 'Fabricante chino GRG Banking. Soporte XFS limitado. Recolección vía SNMP/ISO 8583 TAP únicamente. Concentrado en Distrito Nacional.',
  },
  {
    model: 'NCR Selfserv 6682 RA', subtitle: '≡ SelfServ 82 · Rear Access',
    family: 'SS-66xx Modular', generation: 'Serie 6600 — Modular Rear Access', released: '2020',
    so_status: 'active', so_label: 'Win 10 — Vigente',
    fleet_count: 2, fleet_pct: '0.2%',
    zones: { dn: 2, norte: 0, sur: 0, este: 0 },
    cdm_cassettes: 6, card_reader: 'Motorizado', screen: '17" Touch Widescreen',
    connectivity: 'Gigabit Ethernet',
    feature_pills: ['🏦 Interior', '♻️ Recycler', 'Win 10', 'Rear Access'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: true },
      { label: 'Reciclaje', yes: true }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Alta', reliability_score: 5,
    top_faults: ['OPX_CASSETTE_SWAP'],
    notes: 'Variante Rear Access del 6682, ideal para instalar a través de pared. Idénticas capacidades al FA. Solo 2 unidades en flota.',
  },
  {
    model: 'NCR Selfserv 62', subtitle: 'Interior Modular Full-Function ATM',
    family: 'SS-6x', generation: 'Serie 60 — Modular Legacy', released: '2013',
    so_status: 'eol', so_label: 'Win 7 — EOL',
    fleet_count: 1, fleet_pct: '0.1%',
    zones: { dn: 1, norte: 0, sur: 0, este: 0 },
    cdm_cassettes: 4, card_reader: 'Motorizado', screen: '15" Touch',
    connectivity: '100 Ethernet',
    feature_pills: ['🏦 Interior', '💰 Full-Function', 'Win 7 ⚠️', 'Modular'],
    funcionalidades: [
      { label: 'Retiro', yes: true }, { label: 'Consulta de saldo', yes: true },
      { label: 'Cambio de PIN', yes: true }, { label: 'Depósito', yes: false },
      { label: 'Reciclaje', yes: false }, { label: 'ITM/Video', yes: false },
    ],
    reliability: 'Media', reliability_score: 3,
    top_faults: ['CDM_CASHLOW_01', 'IDC_MOTORERR_11'],
    notes: 'Generación 6 modular (precursora del 6682). Única unidad en la flota actual. Legacy, candidata a sustitución inmediata.',
  },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────
const FAMILY_COLOR: Record<string, string> = {
  'SS-2x Legacy':    '#8b5cf6',
  'SS-3x Mid':       '#6b7280',
  'SS-6x':           '#02AFF0',
  'SS-66xx Modular': '#F89421',
  'SS-8x Current':   '#22c55e',
  'GRG Non-NCR':     '#f43f5e',
}

const RELIABILITY_COLOR: Record<string, string> = {
  Alta: '#22c55e', Media: '#facc15', Baja: '#ef4444',
}

const SO_CONFIG: Record<SOStatus, { label: string; bg: string; color: string; icon: string }> = {
  active:  { label: 'SO Vigente', bg: 'rgba(34,197,94,0.12)',  color: '#22c55e',  icon: '✅' },
  eol:     { label: 'SO Legacy',  bg: 'rgba(239,68,68,0.1)',   color: '#f87171', icon: '⚠️' },
  legacy:  { label: 'SO Legacy',  bg: 'rgba(248,148,33,0.12)', color: '#F89421',  icon: '⚠️' },
}

// Real zone distribution bars
const ZONE_COLORS = ['#264E72', '#02AFF0', '#F89421', '#e74c3c']
const ZONE_LABELS = ['D.Nacional', 'Norte', 'Sur', 'Este']

function ZoneBar({ zones, total }: { zones: ZoneDist; total: number }) {
  const C = useTheme()
  const vals = [zones.dn, zones.norte, zones.sur, zones.este]
  return (
    <div>
      <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>
        Distribución por zona
      </div>
      {/* Segmented bar */}
      <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', height: 6, gap: 1 }}>
        {vals.map((v, i) => (
          v > 0 ? <div key={i} style={{ flex: v, background: ZONE_COLORS[i], minWidth: 2 }} /> : null
        ))}
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
        {vals.map((v, i) =>
          v > 0 ? (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: C.muted }}>
              <span style={{ width: 7, height: 7, borderRadius: 2, background: ZONE_COLORS[i], display: 'inline-block' }} />
              {ZONE_LABELS[i]} {v}
            </span>
          ) : null
        )}
      </div>
    </div>
  )
}

function ReliabilityDots({ score }: { score: number }) {
  const C = useTheme()
  const col = score >= 4 ? C.green : score >= 3 ? C.yellow : C.red
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: i <= score ? col : 'rgba(255,255,255,0.1)' }} />
      ))}
    </div>
  )
}

// ─── Spec Modal ───────────────────────────────────────────────────────────────
function ModelModal({ spec, onClose }: { spec: ModelSpec; onClose: () => void }) {
  const C = useTheme()
  const infocardSrc = modelInfocard(spec.model)
  const iconSrc = modelIcon(spec.model)
  const familyColor = FAMILY_COLOR[spec.family] ?? C.blue
  const soConf = SO_CONFIG[spec.so_status]
  const zones = [spec.zones.dn, spec.zones.norte, spec.zones.sur, spec.zones.este]

  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Ficha técnica ${spec.model}`}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: C.overlay,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        backdropFilter: 'blur(6px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card2,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          maxWidth: 960,
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.65)',
        }}
      >
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 20px', flexShrink: 0,
          background: `linear-gradient(135deg, ${C.primary} 0%, #1b3a57 100%)`,
          borderBottom: `1px solid rgba(255,255,255,0.1)`,
        }}>
          <img src={iconSrc} alt={spec.model} style={{ height: 40, width: 40, objectFit: 'contain', filter: 'brightness(1.2)' }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/atm-models/atm-generic.png' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: '#fff', lineHeight: 1.1 }}>{spec.model}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>{spec.subtitle}</div>
          </div>
          <span style={{
            background: familyColor + '25', border: `1px solid ${familyColor}60`,
            color: familyColor, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700,
          }}>{spec.family}</span>
          <span style={{
            background: soConf.bg, border: `1px solid ${soConf.color}40`,
            color: soConf.color, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600,
          }}>{soConf.icon} {soConf.label}</span>
          <button onClick={onClose} aria-label="Cerrar" style={{
            background: 'rgba(255,255,255,0.1)', border: `1px solid rgba(255,255,255,0.2)`,
            borderRadius: 6, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 14,
            padding: '5px 11px', fontFamily: 'inherit', lineHeight: 1,
          }}>✕</button>
        </div>

        {/* Modal body */}
        <div style={{ display: 'flex', overflow: 'hidden', flex: 1 }}>

          {/* Left: spec-sheet sketch */}
          <div style={{
            width: 340, flexShrink: 0,
            background: '#070d14',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, overflow: 'auto',
            borderRight: `1px solid ${C.border}`,
          }}>
            <img
              src={infocardSrc}
              alt={`Ficha técnica ${spec.model}`}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6 }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/atm-models/atm-generic.png' }}
            />
          </div>

          {/* Right: specs */}
          <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>

            {/* Hero KPIs */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              {[
                { label: 'Unidades en flota', value: String(spec.fleet_count), sub: `${spec.fleet_pct} del parque`, color: C.blue },
                { label: 'Confiabilidad', value: spec.reliability, color: RELIABILITY_COLOR[spec.reliability], dots: true },
                { label: 'SO', value: spec.so_label, color: soConf.color },
                { label: 'Lanzamiento', value: spec.released, color: C.muted },
              ].map((k) => (
                <div key={k.label} style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: 10, padding: '10px 14px', flex: '1 1 100px', minWidth: 90,
                }}>
                  <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{k.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: k.color, lineHeight: 1.1 }}>{k.value}</div>
                  {k.sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{k.sub}</div>}
                  {k.dots && <div style={{ marginTop: 4 }}><ReliabilityDots score={spec.reliability_score} /></div>}
                </div>
              ))}
            </div>

            {/* Zone distribution — 4 colored boxes */}
            <ModalSectionTitle>Distribución por zona</ModalSectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
              {ZONE_LABELS.map((z, i) => (
                <div key={z} style={{
                  borderRadius: 10, padding: '10px', textAlign: 'center',
                  background: ZONE_COLORS[i] + '22',
                  border: `1px solid ${ZONE_COLORS[i]}55`,
                }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: ZONE_COLORS[i], lineHeight: 1 }}>{zones[i]}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: ZONE_COLORS[i], marginTop: 3, opacity: 0.85 }}>{z}</div>
                </div>
              ))}
            </div>

            {/* Tech specs grid */}
            <ModalSectionTitle>Especificaciones técnicas</ModalSectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginBottom: 20 }}>
              {[
                ['Familia', spec.family],
                ['Generación', spec.generation],
                ['Lector de tarjeta', spec.card_reader],
                ['Cassettes CDM', `${spec.cdm_cassettes} cassettes`],
                ['Pantalla', spec.screen],
                ['Conectividad', spec.connectivity],
              ].map(([k, v]) => (
                <div key={k} style={{
                  display: 'flex', justifyContent: 'space-between', gap: 8,
                  borderBottom: `1px solid ${C.border}`, padding: '6px 0',
                }}>
                  <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>{k}</span>
                  <span style={{ fontSize: 11, color: C.text, textAlign: 'right', fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Funcionalidades yes/no */}
            <ModalSectionTitle>Funcionalidades</ModalSectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
              {spec.funcionalidades.map((f) => (
                <span key={f.label} style={{
                  padding: '4px 11px', borderRadius: 7, fontSize: 11, fontWeight: 600,
                  background: f.yes ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${f.yes ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.2)'}`,
                  color: f.yes ? C.green : '#f87171',
                  textDecoration: f.yes ? undefined : 'line-through',
                  opacity: f.yes ? 1 : 0.7,
                }}>
                  {f.label}
                </span>
              ))}
            </div>

            {/* Top faults */}
            <ModalSectionTitle>Fallos más frecuentes</ModalSectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
              {spec.top_faults.map((f) => (
                <span key={f} style={{
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.22)',
                  color: '#f87171', borderRadius: 6, padding: '3px 10px',
                  fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
                }}>{f}</span>
              ))}
            </div>

            {/* Notes */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(248,148,33,0.08), rgba(248,148,33,0.04))',
              borderLeft: `3px solid ${C.orange}`,
              borderRadius: '0 10px 10px 0',
              padding: '10px 14px',
              fontSize: 12, color: '#d4a85a', lineHeight: 1.6,
            }}>
              {spec.notes}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function ModalSectionTitle({ children }: { children: ReactNode }) {
  const C = useTheme()
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8,
      color: C.blue, marginBottom: 10, paddingBottom: 5,
      borderBottom: `2px solid rgba(2,175,240,0.3)`,
    }}>
      {children}
    </div>
  )
}

// ─── Model Card ───────────────────────────────────────────────────────────────
function ModelCard({ spec, onClick }: { spec: ModelSpec; onClick: () => void }) {
  const C = useTheme()
  const [hov, setHov] = useState(false)
  const iconSrc = modelIcon(spec.model)
  const famColor = FAMILY_COLOR[spec.family] ?? C.blue
  const soConf = SO_CONFIG[spec.so_status]

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      aria-label={`Ver ficha técnica ${spec.model}`}
      style={{
        background: C.card,
        border: `2px solid ${hov ? famColor + '80' : C.border}`,
        borderRadius: 16,
        overflow: 'hidden',
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column',
        transition: 'all 0.2s',
        transform: hov ? 'translateY(-3px)' : 'none',
        boxShadow: hov ? `0 8px 32px rgba(0,0,0,0.35)` : 'none',
      }}
    >
      {/* Photo area */}
      <div style={{
        position: 'relative', height: 160,
        background: hov ? '#0d1520' : '#111820',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', flexShrink: 0,
        transition: 'background 0.2s',
      }}>
        <img
          src={iconSrc}
          alt={spec.model}
          style={{
            maxHeight: 140, maxWidth: '82%', objectFit: 'contain',
            transform: hov ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 0.22s',
            filter: hov ? 'brightness(1.15) drop-shadow(0 0 10px rgba(2,175,240,0.25))' : 'brightness(0.95)',
          }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/atm-models/atm-generic.png' }}
        />
        {/* Hover overlay */}
        {hov && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(2,175,240,0.06)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            paddingBottom: 10,
          }}>
            <span style={{
              background: 'rgba(2,175,240,0.85)', color: C.dark,
              fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
              padding: '4px 12px', borderRadius: 12,
            }}>
              Ver ficha técnica →
            </span>
          </div>
        )}
        {/* SO badge top-right */}
        <span style={{
          position: 'absolute', top: 8, right: 8,
          background: soConf.bg, color: soConf.color,
          fontSize: 9, fontWeight: 700,
          padding: '2px 7px', borderRadius: 8,
          backdropFilter: 'blur(4px)',
        }}>
          {soConf.icon} {soConf.label}
        </span>
      </div>

      {/* Card header: series badge + status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px 6px' }}>
        <span style={{
          background: famColor + '25', border: `1px solid ${famColor}55`,
          color: famColor, borderRadius: 20, padding: '2px 9px',
          fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8,
        }}>{spec.family}</span>
        <span style={{ fontSize: 9, color: C.muted }}>{spec.released}</span>
      </div>

      {/* Card body */}
      <div style={{ padding: '0 14px 10px', textAlign: 'center', flex: 1 }}>
        <div style={{ fontWeight: 800, fontSize: 17, color: C.text, marginBottom: 2 }}>{spec.model}</div>
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 10, lineHeight: 1.4 }}>{spec.subtitle}</div>

        {/* Big unit count */}
        <div style={{ fontSize: 34, fontWeight: 800, color: C.text, lineHeight: 1 }}>{spec.fleet_count}</div>
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 10 }}>
          unidades activas · <strong style={{ color: C.blue }}>{spec.fleet_pct}</strong> del parque
        </div>

        {/* Feature pills */}
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          {spec.feature_pills.map((p) => (
            <span key={p} style={{
              background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`,
              borderRadius: 6, padding: '2px 7px', fontSize: 9, fontWeight: 600, color: C.muted,
            }}>{p}</span>
          ))}
        </div>
      </div>

      {/* Zone distribution */}
      <div style={{ padding: '0 14px 12px' }}>
        <ZoneBar zones={spec.zones} total={spec.fleet_count} />
      </div>

      {/* Footer */}
      <div style={{
        padding: '8px 14px 10px',
        borderTop: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <ReliabilityDots score={spec.reliability_score} />
        <span style={{ fontSize: 11, color: C.blue, fontWeight: 600 }}>
          Ver ficha técnica completa →
        </span>
      </div>
    </div>
  )
}

// ─── Main view ────────────────────────────────────────────────────────────────
const FAMILIES = [...new Set(MODEL_SPECS.map((s) => s.family))]
const SO_FILTERS: { id: SOStatus | 'all'; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'active', label: '✅ SO Vigente' },
  { id: 'eol', label: '⚠️ SO EOL' },
  { id: 'legacy', label: '⚠️ Legacy' },
]

export default function ModelosView() {
  const C = useTheme()
  const [activeModel, setActiveModel] = useState<ModelSpec | null>(null)
  const [familyFilter, setFamilyFilter] = useState<string | null>(null)
  const [soFilter, setSoFilter] = useState<SOStatus | 'all'>('all')
  const [search, setSearch] = useState('')

  const filtered = MODEL_SPECS.filter((s) => {
    if (familyFilter && s.family !== familyFilter) return false
    if (soFilter !== 'all' && s.so_status !== soFilter) return false
    if (search && !s.model.toLowerCase().includes(search.toLowerCase()) && !s.subtitle.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const totalUnits = MODEL_SPECS.reduce((sum, s) => sum + s.fleet_count, 0)
  const eolCount = MODEL_SPECS.filter((s) => s.so_status === 'eol' || s.so_status === 'legacy').reduce((sum, s) => sum + s.fleet_count, 0)
  const activeCount = MODEL_SPECS.filter((s) => s.so_status === 'active').reduce((sum, s) => sum + s.fleet_count, 0)

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 16, background: C.dark, display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* KPI header */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {[
          { label: 'Modelos documentados', value: '14', color: C.blue },
          { label: 'Total unidades', value: String(totalUnits), color: C.text, sub: '933 cajeros en campo' },
          { label: 'Con SO Vigente', value: String(activeCount), color: C.green, sub: `${(activeCount/totalUnits*100).toFixed(1)}% del parque` },
          { label: 'Con SO EOL / Legacy', value: String(eolCount), color: C.red, sub: `${(eolCount/totalUnits*100).toFixed(1)}% — candidatos sustitución` },
          { label: 'Fabricantes', value: '2', color: C.muted, sub: 'NCR Atleos + GRG Banking' },
        ].map((k) => (
          <div key={k.label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: '10px 16px', flex: '1 1 140px',
          }}>
            <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: k.color, lineHeight: 1.1 }}>{k.value}</div>
            {k.sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Row 1: family filter + search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>Serie</span>
          <Pill label="Todos" active={!familyFilter} color={C.blue} onClick={() => setFamilyFilter(null)} />
          {FAMILIES.map((fam) => (
            <Pill key={fam} label={fam.replace(' Legacy', '').replace(' Current', '').replace(' Mid', '')} active={familyFilter === fam} color={FAMILY_COLOR[fam]} onClick={() => setFamilyFilter(fam === familyFilter ? null : fam)} />
          ))}
          {/* Search */}
          <div style={{ marginLeft: 'auto', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 11 }}>🔍</span>
            <input
              type="text"
              placeholder="Buscar modelo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                paddingLeft: 28, paddingRight: 12, paddingTop: 6, paddingBottom: 6,
                borderRadius: 20, border: `1.5px solid ${search ? C.blue : C.border}`,
                background: C.card2, color: C.text, fontSize: 12,
                fontFamily: 'inherit', outline: 'none', width: 190,
                transition: 'all 0.18s',
              }}
            />
          </div>
        </div>
        {/* Row 2: SO status filter + count */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>Estado SO</span>
          {SO_FILTERS.map((f) => (
            <Pill
              key={f.id}
              label={f.label}
              active={soFilter === f.id}
              color={f.id === 'active' ? C.green : f.id === 'eol' || f.id === 'legacy' ? C.red : C.blue}
              onClick={() => setSoFilter(f.id as SOStatus | 'all')}
            />
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: C.muted }}>
            <strong style={{ color: C.text }}>{filtered.length}</strong> modelos mostrados
          </span>
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: C.muted }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🏧</div>
          <div style={{ fontSize: 14 }}>No se encontraron modelos con los filtros actuales.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {filtered.map((spec) => (
            <ModelCard key={spec.model} spec={spec} onClick={() => setActiveModel(spec)} />
          ))}
        </div>
      )}

      {/* Modal */}
      {activeModel && <ModelModal spec={activeModel} onClose={() => setActiveModel(null)} />}
    </div>
  )
}

function Pill({ label, active, color, onClick }: { label: string; active: boolean; color: string; onClick: () => void }) {
  const C = useTheme()
  return (
    <button onClick={onClick} style={{
      background: active ? color + '22' : 'transparent',
      border: `1.5px solid ${active ? color : C.border}`,
      color: active ? color : C.muted,
      borderRadius: 20, padding: '5px 13px', fontSize: 11, fontWeight: active ? 700 : 400,
      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
      letterSpacing: active ? 0.3 : 0,
    }}>{label}</button>
  )
}
